import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import ErrorMessage from '../components/ErrorMessage'
import LoadingProgress from '../components/LoadingProgress'
import Calendar from '../components/Calendar'; // Import the Calendar component
import { fetchCalendar } from '../actions/calendars'
import { fetchCustomer } from '../actions/customers'
import CalendarPricesAddForm from '../forms/CalendarPricesAddForm'
import '../styles/CalendarDetailsView.css'

class CalendarDetailsView extends React.Component {
  constructor(props) {
    super(props);
    const currentYear = new Date().getFullYear();
    this.state = {
      startMonth: 10,
      endMonth: 3,
      startYear: currentYear,
      endYear: currentYear,
      minYear: currentYear,
      maxYear: currentYear,
      showControls: false,
    };
  }

  componentDidMount() {
    const { fetchCalendar, fetchCustomer } = this.props;

    fetchCalendar(this.props.match.params.id).then(response => {

      fetchCustomer(response.response.Customer);
    });
  }

  componentDidUpdate(prevProps) {
    // Only initialise month/year range from data the first time the calendar
    // arrives. On subsequent refetches (after add / modify / delete) we keep
    // whatever the user is currently viewing so their position is preserved.
    if (
      !this.rangeInitialized &&
      this.props.calendar &&
      this.props.calendar.PriceCalendar &&
      this.props.calendar.PriceCalendar.length > 0
    ) {
      // For the year dropdown range, use all entries regardless of fill
      const allDates = this.props.calendar.PriceCalendar.map(entry => new Date(entry.date));
      const minYear = new Date(Math.min(...allDates)).getFullYear();
      const maxYear = new Date(Math.max(...allDates)).getFullYear();

      this.rangeInitialized = true;

      // Default view to the current month/year
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      // Find the last month with a filled price value in the current year
      const filledCurrentYear = this.props.calendar.PriceCalendar.filter(entry => {
        const d = new Date(entry.date);
        return d.getFullYear() === currentYear &&
          entry.prices && entry.prices.some(p => p && typeof p.price === 'number');
      });
      const lastFilledMonth = filledCurrentYear.length > 0
        ? new Date(Math.max(...filledCurrentYear.map(e => new Date(e.date)))).getMonth()
        : currentMonth;

      this.setState({
        startMonth: currentMonth,
        endMonth: lastFilledMonth,
        startYear: currentYear,
        endYear: currentYear,
        minYear,
        maxYear,
      });
    } else if (
      this.rangeInitialized &&
      this.props.calendar &&
      this.props.calendar.PriceCalendar &&
      prevProps.calendar?.PriceCalendar !== this.props.calendar.PriceCalendar &&
      this.props.calendar.PriceCalendar.length > 0
    ) {
      // Keep minYear / maxYear in sync (new years might have been added)
      // but do NOT touch the currently displayed startMonth/endMonth/startYear/endYear.
      const dates = this.props.calendar.PriceCalendar.map(entry => new Date(entry.date));
      const minYear = new Date(Math.min(...dates)).getFullYear();
      const maxYear = new Date(Math.max(...dates)).getFullYear();
      if (minYear !== this.state.minYear || maxYear !== this.state.maxYear) {
        this.setState({ minYear, maxYear });
      }
    }
  }

  handleMonthChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: parseInt(value) });
  };

  handleYearChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: parseInt(value) });
  };

  render() {
    const { calendar, error } = this.props;
    const { startMonth, endMonth, startYear, endYear, minYear, maxYear, showControls } = this.state;

    if (error) {
      return <ErrorMessage message={error.message} />;
    }

    // Only block the whole page on the *initial* load (when we have no
    // calendar yet). Subsequent add / modify / delete operations toggle
    // `loading` in the reducer, but we don't want to unmount the view —
    // that is what caused losing the scroll position after each submit.
    if (!calendar) {
      return <LoadingProgress />;
    }

    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    // Generate array of years from minYear to maxYear
    const availableYears = [];
    for (let year = minYear; year <= maxYear; year++) {
      availableYears.push(year);
    }

    const datesForRange = (calendar.PriceCalendar || [])
      .map(pc => { const s = typeof pc.date === 'string' ? pc.date.split('T')[0] : ''; return s || null })
      .filter(Boolean)
      .sort()
    const startDateLabel = datesForRange.length ? new Date(datesForRange[0] + 'T00:00:00Z').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—'
    const endDateLabel = datesForRange.length ? new Date(datesForRange[datesForRange.length-1] + 'T00:00:00Z').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—'
    const entriesCount = (calendar.PriceCalendar || []).length

    const priceValues = (calendar.PriceCalendar || []).reduce((acc, pc) => {
      if (pc.prices && pc.prices.length) pc.prices.forEach(p => { if (p && typeof p.price === 'number') acc.push(p.price) })
      return acc
    }, [])
    const minPrice = priceValues.length ? Math.min(...priceValues) : null
    const maxPrice = priceValues.length ? Math.max(...priceValues) : null
    const fmt = v => v === null ? '—' : v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })

    // View-range stats (currently selected months)
    // Parse year/month from the date string directly to avoid any timezone offset issues
    const parseYM = (date) => {
      const s = typeof date === 'string' ? date : new Date(date).toISOString()
      const parts = s.split('T')[0].split('-')
      return { y: parseInt(parts[0], 10), m: parseInt(parts[1], 10) - 1 }
    }
    const viewEntries = (calendar.PriceCalendar || []).filter(pc => {
      if (!pc.date) return false
      const { y, m } = parseYM(pc.date)
      if (isNaN(y) || isNaN(m)) return false
      if (startYear === endYear) return y === startYear && m >= startMonth && m <= endMonth
      if (y === startYear) return m >= startMonth
      if (y === endYear) return m <= endMonth
      return y > startYear && y < endYear
    })
    const viewPrices = viewEntries.reduce((acc, pc) => {
      if (pc.prices) pc.prices.forEach(p => { if (p && typeof p.price === 'number') acc.push(p.price) })
      return acc
    }, [])
    const viewMin = viewPrices.length ? Math.min(...viewPrices) : null
    const viewMax = viewPrices.length ? Math.max(...viewPrices) : null
    const viewAvg = viewPrices.length ? viewPrices.reduce((s,v)=>s+v,0)/viewPrices.length : null
    const monthNamesShort = [
      'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
      'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
    ]
    const viewLabel = startYear === endYear && startMonth === endMonth
      ? `${monthNamesShort[startMonth]} ${startYear}`
      : `${monthNamesShort[startMonth]} ${startYear} — ${monthNamesShort[endMonth]} ${endYear}`

    // Detect missing days in the viewed range
    const existingDates = new Set(
      (calendar.PriceCalendar || []).map(pc =>
        typeof pc.date === 'string' ? pc.date.split('T')[0] : ''
      ).filter(Boolean)
    )
    const missingDays = []
    const pad = n => String(n).padStart(2, '0')
    for (let y = startYear; y <= endYear; y++) {
      const mStart = y === startYear ? startMonth : 0
      const mEnd = y === endYear ? endMonth : 11
      for (let mo = mStart; mo <= mEnd; mo++) {
        const daysInMonth = new Date(y, mo + 1, 0).getDate()
        for (let d = 1; d <= daysInMonth; d++) {
          const key = `${y}-${pad(mo + 1)}-${pad(d)}`
          if (!existingDates.has(key)) missingDays.push(key)
        }
      }
    }

    return (
      <div className="calendar-details-view">
        <div className="calendar-details-header mb-3">
          <div className="calendar-details-title">
            <h1 className="m-0">
              <i className="fas fa-calendar-alt mr-2"></i>
              <span className="text-vw-light">Calendario</span>
              <span className="text-vw-dark"> {calendar.Name}</span>
            </h1>
          </div>
          <div className="calendar-details-actions d-flex align-items-center" style={{gap: '12px'}}>
            <div className="header-stat-group">
              <span className="header-stat-group-label">Vista actual · {viewLabel}</span>
              <div className="header-stat-pills">
                <span className="header-stat-pill"><i className="fas fa-calendar-check mr-1"></i>{viewEntries.length} días</span>
                <span className="header-stat-pill low"><i className="fas fa-arrow-down mr-1"></i>{fmt(viewMin)}</span>
                <span className="header-stat-pill avg"><i className="fas fa-minus mr-1"></i>{fmt(viewAvg)}</span>
                <span className="header-stat-pill high"><i className="fas fa-arrow-up mr-1"></i>{fmt(viewMax)}</span>
              </div>
            </div>
            <div className="header-stat-divider"></div>
            <div className="header-stat-group">
              <span className="header-stat-group-label">Todo el calendario</span>
              <div className="header-stat-pills">
                <span className="header-stat-pill"><i className="fas fa-calendar-check mr-1"></i>{entriesCount} días</span>
                <span className="header-stat-pill"><i className="fas fa-arrows-alt-h mr-1"></i>{startDateLabel} — {endDateLabel}</span>
                <span className="header-stat-pill low"><i className="fas fa-arrow-down mr-1"></i>{fmt(minPrice)}</span>
                <span className="header-stat-pill high"><i className="fas fa-arrow-up mr-1"></i>{fmt(maxPrice)}</span>
              </div>
            </div>
            <div className="header-stat-divider"></div>
            <button
              className="calendar-toggle-btn"
              onClick={() => this.setState(s => ({ showControls: !s.showControls }))}
            >
              {showControls ? (
                <span key="open"><i className="fas fa-chevron-up mr-1"></i>Ocultar controles</span>
              ) : (
                <span key="closed"><i className="fas fa-chevron-down mr-1"></i>Mostrar controles</span>
              )}
            </button>
          </div>
        </div>

        {showControls && (
        <div className="row mb-3">
          <div className="col-xs-12 col-sm-4 col-md-3 mb-2">
            <div className="ctrl-card">
              <div className="ctrl-card-header"><i className="fas fa-eye mr-1"></i>Vista</div>
              <div className="ctrl-card-body">
                <div className="ctrl-field-group">
                  <label className="ctrl-label">Inicio</label>
                  <div className="ctrl-row">
                    <select className="ctrl-select" name="startMonth" value={startMonth} onChange={this.handleMonthChange}>
                      {monthNames.map((monthName, index) => (
                        <option key={index} value={index}>{monthName}</option>
                      ))}
                    </select>
                    <select className="ctrl-select" name="startYear" value={startYear} onChange={this.handleYearChange}>
                      {availableYears.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="ctrl-field-group">
                  <label className="ctrl-label">Fin</label>
                  <div className="ctrl-row">
                    <select className="ctrl-select" name="endMonth" value={endMonth} onChange={this.handleMonthChange}>
                      {monthNames.map((monthName, index) => (
                        <option key={index} value={index}>{monthName}</option>
                      ))}
                    </select>
                    <select className="ctrl-select" name="endYear" value={endYear} onChange={this.handleYearChange}>
                      {availableYears.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xs-12 col-sm-8 col-md-9 mb-2">
            <div className="ctrl-card">
              <div className="ctrl-card-header"><i className="fas fa-edit mr-1"></i>Actualizar precios</div>
              <div className="ctrl-card-body">
                <CalendarPricesAddForm
                  customer={this.props.customer}
                  calendar={calendar}
                />
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="cib-card mb-3">
          <div className="cib-left">
            <p className="cib-section-title"><i className="fas fa-book-open mr-1"></i>Cómo funciona</p>
            <ul className="cib-list">
              <li><i className="fas fa-circle cib-dot covered"></i>Días con precio configurado aparecen con fondo claro <strong>verdoso</strong>.</li>
              <li><i className="fas fa-circle cib-dot missing"></i>Días sin ningún precio aparecen con fondo <strong>rojizo</strong> — pueden causar errores en reservas.</li>
              <li><i className="fas fa-sliders-h cib-dot neutral"></i>Usa <strong>Mostrar controles</strong> para cambiar el rango de meses visible.</li>
              <li><i className="fas fa-edit cib-dot neutral"></i>Para añadir o eliminar precios, abre los controles y usa el formulario de actualización.</li>
            </ul>
          </div>
          {missingDays.length > 0 && (
            <div className="cib-right">
              <p className="cib-section-title cib-warn-title"><i className="fas fa-exclamation-triangle mr-1"></i>{missingDays.length} día{missingDays.length > 1 ? 's' : ''} sin entrada en la vista actual</p>
              <div className="cib-missing-list">
                {missingDays.slice(0, 30).map(d => (
                  <span key={d} className="cib-missing-day">{new Date(d + 'T00:00:00Z').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' })}</span>
                ))}
                {missingDays.length > 30 && <span className="cib-missing-more">+{missingDays.length - 30} más</span>}
              </div>
            </div>
          )}
        </div>

        <div className="row mb-3">
          <div className="col">
            <Calendar
              startYear={startYear}
              endYear={endYear}
              priceCalendar={calendar.PriceCalendar}
              startMonth={startMonth}
              endMonth={endMonth}
              customer={this.props.customer}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  calendar: state.calendars.calendar,
  customer: state.customers.customer,
  loading: state.calendars.loading,
  error: state.calendars.error,
});

const mapDispatchToProps = dispatch => ({
  fetchCalendar: bindActionCreators(fetchCalendar, dispatch),
  fetchCustomer: bindActionCreators(fetchCustomer, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(CalendarDetailsView);
